import React, { useRef, useMemo, useEffect } from "react";

import { ReportAnswer } from "../../../graphql/types";
import { TemplateQuestionFragment } from "../../../graphql/fragments";

import useTranslations from "../../../hooks/useTranslations";

import { Badge, Textarea, Radio, RadioGroup, Input } from "../../wombat-ui";

import {
  getReportAnswersByRole,
  TemplateQuestionImplProps,
  TemplateQuestionPreviewImplProps,
} from "../TemplateQuestion";
import TemplateQuestionTitle from "./utils/TemplateQuestionTitle";
import Answer from "./utils/Answer";

type TemplateQuestionSelectorSingleOptions = {
  value: string;
  label: string;
};

type TemplateQuestionSelectorSingleValue = {
  selector: string;
  comment: string;
  timeSpent: number;
};

type TemplateQuestionSelectorSingle = {
  options: TemplateQuestionSelectorSingleOptions[];
  defaultValue: TemplateQuestionSelectorSingleValue;
} & TemplateQuestionFragment;

export function isTemplateQuestionSelectorSingle(
  q: TemplateQuestionFragment
): q is TemplateQuestionSelectorSingle {
  return q.type === "TemplateQuestionSelectorSingle";
}

export function parseQuestion(
  question: TemplateQuestionFragment
): TemplateQuestionSelectorSingle {
  return question as TemplateQuestionSelectorSingle;
}

type ReportAnswerSelectorSingle = {
  value: TemplateQuestionSelectorSingleValue;
} & ReportAnswer;

export function parseAnswers(
  answers: ReportAnswer[]
): ReportAnswerSelectorSingle[] {
  return answers as ReportAnswerSelectorSingle[];
}

type TemplateQuestionSelectorSingleProps = TemplateQuestionImplProps;

export const TemplateQuestionSelectorSingle: React.FC<TemplateQuestionSelectorSingleProps> = React.memo(
  ({
    question: rawQuestion,
    answers: rawAnswers,
    mad,
    register,
    status,
    displayIndex,
    role,
    name,
    needsToBeFilled,
    onReview,
    autoFocus,
  }) => {
    const t = useTranslations();

    const answers = useMemo(
      () => getReportAnswersByRole(rawAnswers, parseAnswers, mad),
      [rawAnswers]
    );
    const question = useMemo(() => parseQuestion(rawQuestion), [rawQuestion]);

    const firstRadioRef = useRef(null);
    useEffect(() => {
      if (autoFocus) firstRadioRef.current?.focus();
    }, [firstRadioRef.current, autoFocus]);

    return (
      <div>
        <RadioGroup
          display="row"
          title={
            <TemplateQuestionTitle
              title={question.label}
              description={question.description}
              displayIndex={displayIndex}
              referents={question.referents}
            />
          }
          description={t("reports.singleSelectorQuestion") as string}
        >
          {question.options.map((opt, index) => (
            <Radio
              defaultChecked={
                answers?.[role === "talent" ? "talent" : "referent"]?.[0]?.value
                  ?.selector === opt.value
              }
              key={opt.value}
              name={`${name}.value.selector`}
              label={opt.label}
              id={`${name}.value.selector_${opt.value}`}
              value={opt.value}
              disabled={!needsToBeFilled}
              variant="boxed"
              ref={(ref) => {
                if (index === 0) firstRadioRef.current = ref;
                register(ref);
              }}
            />
          ))}
        </RadioGroup>
        {question.timeTracked && (
          <Input
            label={
              !question.expectedTimeValue
                ? (t("reports.timeSpentInHour") as string)
                : `${t("reports.timeSpentInHour") as string} - ${t(
                    `reports.expectedTime.${question.expectedTimeUnit}`,
                    {
                      count: question.expectedTimeValue,
                    }
                  )}`
            }
            type="number"
            size="small"
            min={0}
            disabled={!needsToBeFilled}
            id={`${question.id}-timeTracked`}
            name={`${name}.timeSpent`}
            ref={register({ valueAsNumber: true })}
            defaultValue={answers?.talent?.[0]?.timeSpent || undefined}
          />
        )}
        {needsToBeFilled && (
          <div className="max-w-2xl pt-4">
            <Textarea
              defaultValue={
                answers?.[role === "talent" ? "talent" : "referent"]?.[0]?.value
                  ?.comment
              }
              label={t("reports.comments") as string}
              id={question.id}
              ref={register()}
              rows={2}
              name={`${name}.value.comment`}
              help={t("reports.commentsHelp") as string}
            />
          </div>
        )}
        {(status === "to_be_filled_evaluator" || onReview) && (
          <div className="px-4 py-3 my-2">
            <h4 className="text-sm font-medium leading-5 text-dark-100">
              {t("reports.talentAnswer")}
            </h4>
            <div className="grid grid-cols-1 gap-3 pt-3">
              {answers.talent.map((a) => (
                <Answer
                  answer={a}
                  answerAs={
                    <Badge>
                      {
                        question.options.find(
                          (o) => o.value === a.value.selector
                        )?.label
                      }
                    </Badge>
                  }
                  withComment
                  comment={a.value.comment}
                  key={a.id}
                />
              ))}
            </div>
          </div>
        )}
        {status !== "to_be_filled_evaluator" && answers.referent.length > 0 && (
          <div className="px-4 py-3 my-2">
            <h4 className="text-sm font-medium leading-5 text-dark-100">
              {t("reports.evaluatorsAnswers", {
                count: answers.referent.length,
              })}
            </h4>
            <div className="grid grid-cols-1 gap-3 pt-3">
              {answers.referent.map((a) => (
                <Answer
                  answer={a}
                  answerAs={
                    <Badge>
                      {
                        question.options.find(
                          (o) => o.value === a.value.selector
                        )?.label
                      }
                    </Badge>
                  }
                  withComment
                  comment={a.value.comment}
                  key={a.id}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }
);

type TemplateQuestionSelectorSinglePreviewProps = TemplateQuestionPreviewImplProps;

export const TemplateQuestionSelectorSinglePreview: React.FC<TemplateQuestionSelectorSinglePreviewProps> = React.memo(
  ({ question: rawQuestion, displayIndex }) => {
    const t = useTranslations();

    const question = useMemo(() => parseQuestion(rawQuestion), [rawQuestion]);

    return (
      <div>
        <RadioGroup
          display="row"
          title={
            <TemplateQuestionTitle
              title={question.label}
              displayIndex={displayIndex}
            />
          }
          description={t("reports.singleSelectorQuestion") as string}
        >
          {question.options.map((opt) => (
            <Radio
              key={`${opt.value}`}
              name={question.id}
              label={opt.label}
              id={`${question.label}_${opt.value}`}
              disabled
              variant="boxed"
            />
          ))}
        </RadioGroup>
      </div>
    );
  }
);
